import React, { useState, useMemo } from 'react';
import api from '../../api';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, ScatterChart, Scatter, ZAxis
} from 'recharts';
import { Users, Briefcase, Calendar, CheckCircle, TrendingUp, Target, Activity, Building2, UserCheck, Inbox, X } from 'lucide-react';
import BatchSkillAnalytics from '../BatchSkillAnalytics';
import CardInfoTooltip from '../CardInfoTooltip';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];
const TpoAnalyticsDashboard = ({ students = [], jobs = [], totalDbJobs = 0, appliedCandidates = [], profile, setActiveTab, setTargetDriveJobId }) => {
  const [showAllJobs, setShowAllJobs] = useState(false);
  const [hidePendingApprovals, setHidePendingApprovals] = useState(() => sessionStorage.getItem('hidePendingApprovalsBanner') === 'true');
  const stats = useMemo(() => {
    const totalStudents = students.length;
    const totalJobs = totalDbJobs || jobs.length; // Fallback to jobs.length if API hasn't loaded yet

    // Determine pending jobs
    const pendingJobs = jobs.filter(j => {
      const s = (j.status || '').toUpperCase();
      return s === 'PENDING';
    }).length;

    // Scheduled drives (jobs with a schedule_date or status scheduled or is_scheduled flag)
    const scheduledDrives = jobs.filter(j => {
      const s = (j.status || '').toUpperCase();
      return s === 'SCHEDULED' || j.drive_date || j.schedule_date || j.is_scheduled;
    }).length;

    // Gender Distribution
    const genderCount = {};
    students.forEach(s => {
      let g = s.gender || 'Unknown';
      // normalize
      if (g.toLowerCase().startsWith('m')) g = 'Male';
      else if (g.toLowerCase().startsWith('f')) g = 'Female';
      else if (g.toLowerCase() !== 'unknown') g = 'Other';
      else g = 'Unknown';

      genderCount[g] = (genderCount[g] || 0) + 1;
    });
    const genderData = Object.keys(genderCount).map(k => ({ name: k, value: genderCount[k] }));

    // 1. Branch-wise Employability Matrix
    const branchStats = {};
    const DEFAULT_MARKET_REQUIREMENTS = [
      { name: 'Python', score: 8 }, { name: 'React', score: 7 }, { name: 'SQL', score: 7 },
      { name: 'Communication', score: 8 }, { name: 'Problem Solving', score: 9 }
    ];
    const totalMarket = DEFAULT_MARKET_REQUIREMENTS.reduce((sum, req) => sum + req.score, 0);

    const parseScore = (val) => {
      if (!val) return 0;
      if (typeof val === 'number') return val;
      const str = String(val).toLowerCase();
      if (str === 'expert') return 9;
      if (str === 'advanced' || str === 'high') return 7;
      if (str === 'intermediate' || str === 'medium') return 5;
      if (str === 'beginner' || str === 'low') return 3;
      const parsed = parseFloat(str);
      return isNaN(parsed) ? 0 : parsed;
    };

    students.forEach(student => {
      let b = student.branch || 'Unknown';
      if (b.length > 25) b = b.substring(0, 22) + '...';

      const profile = student.student_profile || student;
      let skillsList = [];
      if (Array.isArray(profile.ai_skillset)) skillsList = skillsList.concat(profile.ai_skillset);
      if (Array.isArray(profile.ai_toolset)) skillsList = skillsList.concat(profile.ai_toolset);

      let totalStudentScore = 0;
      DEFAULT_MARKET_REQUIREMENTS.forEach(req => {
        const studentSkill = skillsList.find(s => s.name && s.name.toLowerCase() === req.name.toLowerCase());
        const rawScore = studentSkill ? parseScore(studentSkill.score || studentSkill.level) : 0;
        totalStudentScore += Math.min(rawScore, req.score);
      });
      const employabilityScore = totalMarket > 0 ? (totalStudentScore / totalMarket) * 100 : 0;

      if (!branchStats[b]) branchStats[b] = { count: 0, totalScore: 0 };
      branchStats[b].count += 1;
      branchStats[b].totalScore += employabilityScore;
    });

    const branchEmployabilityData = Object.keys(branchStats).map(k => ({
      name: k,
      students: branchStats[k].count,
      readiness: Math.round(branchStats[k].totalScore / branchStats[k].count)
    })).sort((a, b) => b.students - a.students).slice(0, 6);

    // 2. Placement Hiring Funnel
    let appliedCount = 0;
    let approvedCount = 0;
    let underReviewCount = 0;
    let shortlistedCount = 0;
    let offeredCount = 0;
    let hiredCount = 0;
    let rejectedCount = 0;
    let declinedCount = 0;
    let lapsedCount = 0;
    let openAppCount = 0;

    let totalAppliedCount = 0;

    appliedCandidates.forEach(app => {
      totalAppliedCount++; // cumulative total for top KPIs
      const s = (app.status || '').toUpperCase();

      const isHired = s === 'SELECTED' || s === 'HIRED' || s === 'PLACED';
      const isOffered = s === 'OFFERED';
      const isDeclined = s === 'OFFER_DECLINED';
      const isLapsed = s === 'OFFER_LAPSED';
      const isShortlisted = s === 'SHORTLISTED' || s === 'INTERVIEW';
      const isUnderReview = s === 'UNDER_REVIEW' || s === 'UNDER REVIEW';
      const isApproved = s === 'APPROVED';
      const isRejected = s === 'REJECTED';

      if (isHired) hiredCount++;
      else if (isOffered) offeredCount++;
      else if (isDeclined) declinedCount++;
      else if (isLapsed) lapsedCount++;
      else if (isShortlisted) shortlistedCount++;
      else if (isUnderReview) underReviewCount++;
      else if (isApproved) approvedCount++;
      else if (isRejected) rejectedCount++;
      else appliedCount++; // Only those whose exact status is still 'APPLIED' or 'PENDING'

      if (s !== 'REJECTED' && !isHired && !isOffered && !isDeclined && !isLapsed && s !== 'WITHDRAWN') openAppCount++;
    });

    const hiringFunnelData = [
      { stage: 'Applied', count: appliedCount, fill: '#3b82f6' },
      { stage: 'Approved', count: approvedCount, fill: '#0ea5e9' },
      { stage: 'Under Review', count: underReviewCount, fill: '#6366f1' },
      { stage: 'Shortlisted', count: shortlistedCount, fill: '#8b5cf6' },
      { stage: 'Offered', count: offeredCount, fill: '#84cc16' },
      { stage: 'Hired', count: hiredCount, fill: '#10b981' },
      { stage: 'Declined', count: declinedCount, fill: '#f59e0b' },
      { stage: 'Lapsed', count: lapsedCount, fill: '#64748b' },
      { stage: 'Rejected', count: rejectedCount, fill: '#ef4444' }
    ];

    // 3. Corporate Partner Engagement
    const companyEngagement = {};
    jobs.forEach(j => {
      const comp = j.company_name || 'Unknown Partner';
      if (!companyEngagement[comp]) companyEngagement[comp] = { company: comp, jobsPosted: 0, applications: 0 };
      companyEngagement[comp].jobsPosted += 1;
    });
    appliedCandidates.forEach(app => {
      const comp = app.company_name || 'Unknown Partner';
      if (!companyEngagement[comp]) companyEngagement[comp] = { company: comp, jobsPosted: 0, applications: 0 };
      companyEngagement[comp].applications += 1;
    });

    const partnerEngagementData = Object.values(companyEngagement)
      .filter(c => c.company !== 'Unknown Partner')
      .map(c => ({
        ...c,
        jitteredJobs: c.jobsPosted + (Math.random() - 0.5) * 0.4,
        jitteredApps: c.applications + (Math.random() - 0.5) * 0.4,
        engagementScore: (c.jobsPosted * 10) + c.applications // simple bubble size metric
      }));

    return {
      totalStudents, totalJobs, pendingJobs, scheduledDrives,
      totalApplications: totalAppliedCount,
      placedCount: hiredCount + offeredCount + declinedCount + lapsedCount, openAppCount,
      branchEmployabilityData, hiringFunnelData, partnerEngagementData
    };
  }, [students, jobs, appliedCandidates]);

  const warningJobs = jobs.filter(j => j.sla_warning_15h === true);

  return (
    <div className="tpo-analytics-container" style={{ paddingBottom: '40px' }}>
      <div className="faculty-page-heading" style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2>Welcome back, {profile?.tpo_profile?.full_name || 'Placement Officer'}!</h2>
          <p>Placement Command Center & Analytics Overview.</p>
        </div>
      </div>

      {!hidePendingApprovals && warningJobs.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '12px', padding: '16px 20px', marginBottom: '28px', display: 'flex', flexDirection: 'column', gap: '12px', position: 'relative' }}>
          <button
            onClick={() => {
              sessionStorage.setItem('hidePendingApprovalsBanner', 'true');
              setHidePendingApprovals(true);
            }}
            style={{ position: 'absolute', top: '12px', right: '12px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#991b1b', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            title="Dismiss until logout"
          >
            <X size={20} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingRight: '24px' }}>
            <div style={{ color: '#ef4444' }}><Activity size={24} /></div>
            <div>
              <h4 style={{ margin: 0, color: '#991b1b', fontSize: '1.1rem', fontWeight: 'bold' }}>Action Required: Pending Approvals</h4>
              <p style={{ margin: '4px 0 0', color: '#b91c1c', fontSize: '0.9rem' }}>
                You have <strong>{warningJobs.length}</strong> job(s) pending approval for over 15 hours. Please review and schedule placement drives to keep the placement process moving smoothly.
              </p>
            </div>
          </div>

          <div style={{ paddingLeft: '40px', display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '8px' }}>
            {warningJobs.map((job) => (
              <div key={job.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', padding: '12px 16px', borderRadius: '8px', border: '1px solid #fca5a5' }}>
                <div>
                  <h5 style={{ margin: 0, color: '#7f1d1d', fontSize: '1rem', fontWeight: '600' }}>{job.job_name}</h5>
                  <div style={{ fontSize: '0.85rem', color: '#991b1b', marginTop: '2px' }}>Company: {job.company_name}</div>
                </div>
                <button
                  onClick={() => {
                    if (setTargetDriveJobId) setTargetDriveJobId(job.id);
                    if (setActiveTab) setActiveTab('drives');
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                  }}
                >
                  View Job Details
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(14, 165, 233, 0.3)' }}><Users size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Total Students" content="The total count of registered students in the system." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Students
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.totalStudents}</h3>
          </div>
        </div>

        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(245, 158, 11, 0.3)' }}><Briefcase size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Total Jobs" content="Overall number of job postings available or previously posted." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Jobs
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.totalJobs}</h3>
          </div>
        </div>

        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #10b981, #34d399)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(16, 185, 129, 0.3)' }}><CheckCircle size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Pending Jobs" content="Number of job openings currently pending approval from the university." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Open Jobs
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.pendingJobs}</h3>
          </div>
        </div>




      </div>



      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', marginBottom: '40px' }}>
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(139, 92, 246, 0.3)' }}><Calendar size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Placement Drives" content="Total number of scheduled or ongoing placement drives." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Drives
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.scheduledDrives}</h3>
          </div>
        </div>

        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #ef4444, #f87171)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)' }}><Users size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Student Applications" content="Total number of job applications submitted by students across all roles." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Applications
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.totalApplications}</h3>
          </div>
        </div>

        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)', padding: '24px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: '18px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', transition: 'transform 0.2s', cursor: 'pointer', position: 'relative' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-4px)'} onMouseLeave={e => e.currentTarget.style.transform = 'none'}>
          <div style={{ background: 'linear-gradient(135deg, #14b8a6, #2dd4bf)', padding: '16px', borderRadius: '16px', color: '#fff', boxShadow: '0 8px 16px rgba(20, 184, 166, 0.3)' }}><UserCheck size={28} strokeWidth={2.5} /></div>
          <CardInfoTooltip title="Total Offers" content="Total number of students who have been offered a position (including declined/lapsed)." />
          <div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Total Offers
            </p>
            <h3 style={{ margin: '6px 0 0', fontSize: '2rem', color: '#0f172a', fontWeight: '800' }}>{stats.placedCount}</h3>
          </div>
        </div>
      </div>

      {/* Batch Skill vs Market Demand Analytics */}
      <BatchSkillAnalytics students={students} jobs={jobs} />

      {/* Charts Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginTop: '24px' }}>

        {/* Branch-wise Employability Matrix */}
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', padding: '28px', borderRadius: '24px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#0ea5e9', boxShadow: '0 0 10px rgba(14, 165, 233, 0.5)' }}></div>
            Branch Employability Matrix
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '0.85rem', color: '#64748b' }}>Comparing branch size vs. average market readiness.</p>
          <div style={{ height: 320 }}>
            {stats.branchEmployabilityData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={stats.branchEmployabilityData} margin={{ top: 20, right: 20, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b', fontWeight: 600 }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 12, fill: '#10b981' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(248, 250, 252, 0.6)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', padding: '12px 16px' }} />
                  <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} iconType="circle" />
                  <Bar yAxisId="left" dataKey="students" name="Total Students" fill="#0ea5e9" radius={[6, 6, 0, 0]} maxBarSize={40} />
                  <Line yAxisId="right" type="monotone" dataKey="readiness" name="Avg Readiness (%)" stroke="#10b981" strokeWidth={3} dot={{ r: 5, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 7 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 500 }}>No data available</div>
            )}
          </div>
        </div>

        {/* Placement Hiring Funnel */}
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', padding: '28px', borderRadius: '24px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ec4899', boxShadow: '0 0 10px rgba(236, 72, 153, 0.5)' }}></div>
            Placement Hiring Funnel
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '0.85rem', color: '#64748b' }}>Pipeline conversion from eligible to placed.</p>
          <div style={{ height: 320 }}>
            {stats.hiringFunnelData.some(d => d.count > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.hiringFunnelData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#f1f5f9" />
                  <XAxis type="number" hide={true} />
                  <YAxis dataKey="stage" type="category" tick={{ fontSize: 12, fill: '#334155', fontWeight: 600 }} width={90} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: 'rgba(248, 250, 252, 0.6)' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', padding: '12px 16px' }} />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]} maxBarSize={40} label={{ position: 'right', fill: '#64748b', fontSize: 12, fontWeight: 700 }}>
                    {stats.hiringFunnelData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 500 }}>No candidates applied yet</div>
            )}
          </div>
        </div>

        {/* Corporate Partner Engagement */}
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #fcfcfd)', padding: '28px', borderRadius: '24px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', marginBottom: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6', boxShadow: '0 0 10px rgba(139, 92, 246, 0.5)' }}></div>
            Corporate Partner Engagement
          </h3>
          <p style={{ margin: '0 0 24px', fontSize: '0.85rem', color: '#64748b' }}>Jobs posted vs. applications received.</p>
          <div style={{ height: 320 }}>
            {stats.partnerEngagementData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: -20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" dataKey="jitteredJobs" name="Jobs Posted" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} padding={{ left: 20, right: 20 }} allowDecimals={false} tickFormatter={(val) => Math.round(val)} />
                  <YAxis type="number" dataKey="jitteredApps" name="Applications" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} padding={{ top: 20, bottom: 20 }} allowDecimals={false} tickFormatter={(val) => Math.round(val)} />
                  <ZAxis type="number" dataKey="engagementScore" range={[20, 250]} name="Engagement" />
                  <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div style={{ background: 'rgba(255, 255, 255, 0.95)', padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' }}>
                          <p style={{ margin: '0 0 8px', fontWeight: '700', color: '#1e293b', fontSize: '0.95rem' }}>{data.company}</p>
                          <p style={{ margin: '0 0 4px', fontSize: '0.85rem', color: '#64748b' }}>Jobs Posted: <strong style={{ color: '#0ea5e9' }}>{data.jobsPosted}</strong></p>
                          <p style={{ margin: '0', fontSize: '0.85rem', color: '#64748b' }}>Applications: <strong style={{ color: '#8b5cf6' }}>{data.applications}</strong></p>
                        </div>
                      );
                    }
                    return null;
                  }} />
                  <Scatter name="Partners" data={stats.partnerEngagementData} fill="#8b5cf6" fillOpacity={0.7} stroke="#7c3aed" strokeWidth={2}>
                    {stats.partnerEngagementData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Scatter>
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 500 }}>No partnership data available</div>
            )}
          </div>
        </div>

      </div>
      {/* Recruitment Status Table */}
      <div style={{ marginTop: '24px', background: '#ffffff', padding: '24px', borderRadius: '16px', border: '1px solid rgba(226, 232, 240, 0.6)', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '1.15rem', color: '#1e293b', margin: 0, fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building2 size={20} style={{ color: '#ef4444' }} />
            Recruitment Status
          </h3>
          <button
            onClick={() => setShowAllJobs(!showAllJobs)}
            style={{ background: 'transparent', border: 'none', color: '#0f172a', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer', outline: 'none' }}
          >
            {showAllJobs ? 'View less' : 'View all'}
          </button>
        </div>

        <div style={{ overflowX: 'auto', maxHeight: showAllJobs ? '600px' : 'none', overflowY: showAllJobs ? 'auto' : 'visible' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead style={{ position: showAllJobs ? 'sticky' : 'static', top: 0, background: '#ffffff', zIndex: 10 }}>
              <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem' }}>Jobs</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Applied</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Under Review</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Shortlisted</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Offered</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Hired</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Declined / Lapsed</th>
                <th style={{ padding: '12px 16px', fontWeight: '700', color: '#1e293b', fontSize: '0.85rem', textAlign: 'center' }}>Rejected</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const sortedJobs = [...jobs].sort((a, b) => {
                  const aApps = appliedCandidates.filter(app => (app.job_id === a.id) || (app.job_name === a.job_name)).length;
                  const bApps = appliedCandidates.filter(app => (app.job_id === b.id) || (app.job_name === b.job_name)).length;
                  return bApps - aApps;
                });
                return (showAllJobs ? sortedJobs : sortedJobs.slice(0, 10)).map((job, idx) => {
                  const jobApps = appliedCandidates.filter(a => (a.job_id === job.id) || (a.job_name === job.job_name));
                  const total = jobApps.length;
                  let underReview = 0;
                  let short = 0;
                  let offered = 0;
                  let hired = 0;
                  let declinedLapsed = 0;
                  let rejected = 0;
                  jobApps.forEach(a => {
                    const s = (a.status || '').toUpperCase();
                    if (s === 'UNDER_REVIEW' || s === 'PENDING') underReview++;
                    else if (s === 'SHORTLISTED' || s === 'INTERVIEW') short++;
                    else if (s === 'OFFERED') offered++;
                    else if (s === 'HIRED' || s === 'SELECTED' || s === 'PLACED') hired++;
                    else if (s === 'OFFER_DECLINED' || s === 'OFFER_LAPSED') declinedLapsed++;
                    else if (s === 'REJECTED') rejected++;
                  });

                  // Generate a consistent dummy metric if there's no actual data, just to match the vibe of the screenshot where there are small numbers
                  const getDummyIfZero = (val, maxRandom) => {
                    // For the sake of matching the user's expected "active" table look without full backend support yet
                    // But we'll prioritize actual data. 
                    return val;
                  };

                  return (
                    <tr key={job.id || idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px', fontSize: '0.9rem', color: '#0f172a', fontWeight: '600' }}>{job.job_name || job.title}</td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#0d9488', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600' }}>{total}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#0ea5e9', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: underReview > 0 ? 1 : 0.6 }}>{underReview}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#0f766e', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: short > 0 ? 1 : 0.6 }}>{short}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#8b5cf6', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: offered > 0 ? 1 : 0.6 }}>{offered}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#10b981', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: hired > 0 ? 1 : 0.6 }}>{hired}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#f59e0b', color: '#fff', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: declinedLapsed > 0 ? 1 : 0.6 }}>{declinedLapsed}</span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: '32px', padding: '4px 8px', background: '#cbd5e1', color: '#475569', borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', opacity: rejected > 0 ? 1 : 0.6 }}>{rejected}</span>
                      </td>
                    </tr>
                  );
                });
              })()}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>No jobs available to display</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TpoAnalyticsDashboard;
